import React, { useMemo } from "react";

import { faLink } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Big from "big.js";
import { Formik, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { DeploymentBusinessForm } from "components/Business/BusinessForm/DeploymentBusinessForm";
import { TemporaryBusinessMessage } from "components/Business/BusinessForm/TemporaryBusinessMessage";
import { AddButton as SyncButton } from "components/Button/CollapseInlineButton";
import { Button, SecondaryButton } from "components/Form";
import { addressValidationSchema } from "components/Form/Fields/AddressForm";
import { BankingDetailsValidationSchema } from "components/Form/Fields/BankingDetailsForm";
import { mapIncomingTerms } from "components/Form/FormikControls/TradingTerms/utils";
import { tradingTermValidation } from "components/Form/FormikControls/TradingTerms/validation";
import { vendorCommissionBandsValidation } from "components/Form/FormikControls/VendorCommissionBands";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { AuditLogTypes } from "constants/auditLog";
import { ApiModel } from "constants/loading";
import { DeploymentPermissions } from "constants/permissions";

import { getBusinessSubType } from "lib/businesses";
import {
  mapBusinessValuesToInsuranceType,
  mapDefaultDeploymentTransitInsuranceToBusinessValues,
} from "lib/insuranceRates";
import { getSelectSyncDataHashRoute } from "lib/navigation";
import { ABNValidator, OptionalACNValidator } from "lib/validation";

import {
  getActiveLivestockAgentDeployment,
  getActiveSaleyardAdminSaleyard,
  getBusinessRelationsByBusinessId,
  getBusinessTradingTermsById,
  getDefaultBillingSettings,
  getDefaultBuyerSplitByBusinessId,
  getDefaultVendorSplitByBusinessId,
  getInterestSettingsByBusinessId,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  getVendorCommissionBandsByDeploymentBusinessId,
  selectRoleDeployments,
} from "selectors";

import {
  useHasDefaultBuyerSplitPermission,
  useHasDefaultVendorSplitPermission,
  useHasDeploymentPermission,
  useSomeHasPermission,
} from "hooks/useHasPermission";

const defaultSplitValidation = Yup.object().test(
  (values, { createError, path }) => {
    const errors = [];
    if (values) {
      const totalAmount = values.splits.reduce(
        (total, transactionSplit) =>
          total.plus(new Big(transactionSplit.amount || 0)),
        new Big(0),
      );

      if (values.splits.some(split => !split.businessId)) {
        errors.push("All splits must have a business.");
      }

      if (values.splits.some(split => !split.amount)) {
        errors.push("All splits must have an amount.");
      }

      if (!totalAmount.eq(1)) {
        errors.push(
          `Split percentages must add up to 100% (${new Big(1)
            .minus(totalAmount)
            .times(100)}% difference).`,
        );
      }
    }

    if (errors.length > 0) {
      return createError({
        path: `${path}.splits`,
        message: errors.join(" "),
      });
    } else {
      return true;
    }
  },
);

const getBusinessValidationSchema = (
  options = {
    includeVendorCommissionBands: false,
    includeDefaultBuyerSplit: false,
    includeDefaultVendorSplit: false,
  },
) => {
  const shape = {
    name: Yup.string().max(100).required("Required"),
    publicDisplayName: Yup.string()
      .label("Public Display Name")
      .max(200)
      .nullable(),
    shortCode: Yup.string().label("Agent Account Reference").max(10).nullable(),
    shortCodeSaleyard: Yup.string()
      .label("Saleyard Account Reference")
      .max(10)
      .nullable(),
    slug: Yup.string()
      .label("Agent Account Reference")
      .matches(
        /^[[A-Za-z0-9\-_]+$/,
        "Agent Account Reference can only contain letters, numbers, underscores and hyphens",
      )
      .max(20)
      .nullable(),
    shortCodeAuctionsPlus: Yup.string()
      .label("Auctions Plus Shortcode")
      .max(50)
      .nullable(),
    abn: ABNValidator,
    acn: OptionalACNValidator,
    bankingDetails: BankingDetailsValidationSchema.nullable(),
    address: addressValidationSchema.nullable(),
    bpayCustomerReferenceNumber: Yup.string()
      .matches(
        /^[0-9]{3,20}$/,
        "CRN must be all digits and between 3 and 20 in length",
      )
      .nullable(),
    generateBpayCRN: Yup.boolean(),
    myobCustomerCardId: Yup.string()
      .label("Customer Card Id")
      .max(15)
      .nullable(),
    myobSupplierCardId: Yup.string()
      .label("Supplier Card Id")
      .max(15)
      .nullable(),
    identicalCardIds: Yup.mixed().test(
      "Customer Card Ids are not identical",
      "The Customer Card ID and Supplier Card ID should not be identical.",
      (value, context) => {
        if (!context) {
          return;
        }
        // make sure customer card id and supplier card id are not identical
        const { myobCustomerCardId, myobSupplierCardId } = context.parent;
        if (
          myobCustomerCardId != null &&
          myobSupplierCardId != null &&
          myobCustomerCardId === myobSupplierCardId
        ) {
          return false;
        }
        return true;
      },
    ),
    tradingTerms: tradingTermValidation,
  };
  if (options.includeVendorCommissionBands) {
    shape.vendorCommissionBands = vendorCommissionBandsValidation;
  }

  if (options.includeDefaultBuyerSplit) {
    shape.defaultBuyerSplit = defaultSplitValidation;
  }
  if (options.includeDefaultVendorSplit) {
    shape.defaultVendorSplit = defaultSplitValidation;
  }

  return Yup.object().shape(shape);
};

const blankDefaults = {
  address: {},
  bankingDetails: null,
  businessUsers: [],
  buyerWays: [],
  emailRecipients: [],
  generateBpayCRN: true,
  isActive: true,
  isBuyer: false,
  isTopBuyerCattle: false,
  isTopBuyerSheep: false,
  isTransporter: false,
  isVendor: false,
  properties: [],
  relationships: [],
  shortCodeSaleyard: "",
  shortCode: "",
  shortCodeAuctionsPlus: "",
};

const Form = props => {
  const { closeSelf, business, handleSubmit, defaultAddressSearch } = props;

  const formikProps = useFormikContext();
  const { dirty, initialValues, isValid, setFieldValue, values } = formikProps;
  const { abn: initialAbn } = initialValues;
  const { abn } = values;

  const hasSyncAllPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermissions.featureSyncAllAlternativeBusinessFields,
  );

  // Hide most of the contents of this page when editing a temporary busines.
  // Clicking the "Convert Business" action will change the form to its normal state, and set the value
  // in the form ready for pushing back to the server.
  const [isTemporary, setIsTemporary] = React.useState(
    business?.isTemporary || false,
  );
  const setNotIsTemporary = () => {
    setIsTemporary(false);
    setFieldValue("isTemporary", false);
  };

  const doHandleClose = () => {
    // if (touched) {
    //   // TODO changes Dialog.
    // }
    closeSelf();
  };

  const [isSubFormActive, setIsSubFormActive] = React.useState(false);
  const submitDisabled = !(dirty && isValid) || isSubFormActive || isTemporary;
  // template string comparison to handle mixed types
  const abnDirty = dirty && `${initialAbn}` !== `${abn}`;

  const titleActionString = business
    ? `Edit ${getBusinessSubType(business)} "${business.name}"`
    : "Create Business";
  const submitTitle = abnDirty && !!abn ? "Submit & Query ABR" : "Submit";

  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);

  const parentObjectSelector = isLivestockAgent
    ? getActiveLivestockAgentDeployment
    : getActiveSaleyardAdminSaleyard;
  const parentObject = useSelector(parentObjectSelector) || {};

  const auditLogLinkProps = useMemo(() => {
    if (isLivestockAgent) {
      return {
        auditLogType: AuditLogTypes.DEPLOYMENT_BUSINESS,
        queryParams: {
          deploymentId: parentObject.id,
        },
      };
    }
    if (isSaleyardAdmin) {
      return {
        auditLogType: AuditLogTypes.SALEYARD_BUSINESS,
        queryParams: {
          saleyardId: parentObject.id,
        },
      };
    }
    return {};
  }, [isLivestockAgent, isSaleyardAdmin, parentObject.id]);

  return (
    <ZoomyDialog
      open
      onClose={doHandleClose}
      maxWidth="xl"
      scroll="body"
      fullWidth
    >
      <WaitForSync requiredData={[ApiModel.BUSINESSES, ApiModel.AGENCIES]}>
        <DialogTitle onClose={doHandleClose}>
          {business && (
            <AuditLogLink
              dataId={business.id}
              returnTo={window.location.hash}
              {...auditLogLinkProps}
            />
          )}
          &nbsp;
          {titleActionString}{" "}
          {hasSyncAllPermission && business && (
            <SyncButton
              to={`#${getSelectSyncDataHashRoute(
                business.id,
                window.location.hash,
              )}`}
              data-tour=""
            >
              <FontAwesomeIcon icon={faLink} /> Sync All
            </SyncButton>
          )}
          <AdminLink model="business" cbid={business?.id} />
        </DialogTitle>
        <DialogContent dividers form="true">
          {isTemporary ? (
            <TemporaryBusinessMessage setNotIsTemporary={setNotIsTemporary} />
          ) : (
            <DeploymentBusinessForm
              {...props}
              setIsSubFormActive={setIsSubFormActive}
              defaultAddressSearch={defaultAddressSearch}
            />
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={doHandleClose}>Cancel</SecondaryButton>
          <Button
            data-tour="submit"
            onClick={() => {
              !submitDisabled && handleSubmit(values);
            }}
            disabled={submitDisabled}
          >
            {submitTitle}
          </Button>
        </DialogActions>
      </WaitForSync>
    </ZoomyDialog>
  );
};

const FormikedForm = ({
  business,
  closeSelf,
  defaultTab,
  handleSubmit,
  defaultAddressSearch,
}) => {
  const initialValues = business ? { ...business } : { ...blankDefaults };

  // Vendor Commission Bands
  const hasVendorCommissionsFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureVendorCommissions,
  );

  const vendorCommissionBands =
    useSelector(
      getVendorCommissionBandsByDeploymentBusinessId(
        business?.deploymentBusinessId,
      ),
    ) || [];

  if (hasVendorCommissionsFeature) {
    initialValues.vendorCommissionBands = vendorCommissionBands;
  }

  // Vendor Splits
  const hasDefaultVendorSplitsFeature = useHasDefaultVendorSplitPermission();

  const defaultVendorSplit = useSelector(
    getDefaultVendorSplitByBusinessId(business?.id),
  );
  if (hasDefaultVendorSplitsFeature) {
    initialValues.defaultVendorSplit = defaultVendorSplit || undefined;
  }

  // Buyer Splits
  const hasDefaultBuyerSplitsFeature = useHasDefaultBuyerSplitPermission();

  const defaultBuyerSplit = useSelector(
    getDefaultBuyerSplitByBusinessId(business?.id),
  );

  if (hasDefaultBuyerSplitsFeature) {
    initialValues.defaultBuyerSplit = defaultBuyerSplit || undefined;
  }

  // Interest
  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureInterest,
  );

  const interestSettings =
    useSelector(getInterestSettingsByBusinessId(business?.id)) || [];

  if (hasInterestFeature) {
    if (interestSettings.length === 0) {
      initialValues.interestSettings = [
        {
          debitInterestRate: 0,
          creditInterestRate: 0,
          daysOverDueThreshold: 0,
          id: uuidv4(),
        },
      ];
    } else {
      initialValues.interestSettings = interestSettings;
    }
  }

  const relationships = useSelector(
    getBusinessRelationsByBusinessId(business?.id),
  );

  // Relationships.
  const hasRelationshipsFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureBusinessRelations,
  );
  if (hasRelationshipsFeature) {
    initialValues.relationships = relationships || [];
  } else {
    // As the API may still be backward compatibily sending the relationships down in the
    // business payload, and we don't want to send it back unless we're certain - if I don't
    // have the permission make sure I don't throw it back.
    initialValues.relationships = undefined;
  }

  const tradingTermsLookup = useSelector(getBusinessTradingTermsById);
  const tradingTerms = tradingTermsLookup[business?.id] || [];
  initialValues.tradingTerms = tradingTerms.map(term => mapIncomingTerms(term));

  const billingSettings = useSelector(getDefaultBillingSettings);

  // If we have a default BillingSettings, use them for the transit insurance default
  if (billingSettings) {
    const transitInsuranceDefault =
      billingSettings?.transitInsuranceApplicationDefault;

    // If we have no business we are creating one,
    // so set the default from the deployment, and map it into initial values
    if (!business) {
      initialValues.transitInsuranceApplicationDefault =
        transitInsuranceDefault;
      const transitInsuranceRates =
        mapDefaultDeploymentTransitInsuranceToBusinessValues(
          transitInsuranceDefault,
        );
      Object.assign(initialValues, transitInsuranceRates);
    } else {
      // Otherwise we are editing, so determine the values from the business
      // and set it in the initial values.
      const mappedTransitInsuranceValue =
        mapBusinessValuesToInsuranceType(initialValues) ||
        transitInsuranceDefault;
      initialValues.transitInsuranceApplicationDefault =
        mappedTransitInsuranceValue;
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getBusinessValidationSchema({
        includeVendorCommissionBands: hasVendorCommissionsFeature,
        includeDefaultBuyerSplit: hasDefaultBuyerSplitsFeature,
        includeDefaultVendorSplit: hasDefaultVendorSplitsFeature,
      })}
      // Enable reinitialise so when we get pusher updates, the form is updated.
      enableReinitialize
    >
      <Form
        closeSelf={closeSelf}
        business={business}
        defaultTab={defaultTab}
        handleSubmit={handleSubmit}
        defaultAddressSearch={defaultAddressSearch}
      />
    </Formik>
  );
};

export default FormikedForm;
